/* Query hooks for the Administration console.
 *
 * Separate from queries.ts on purpose. The console runs on its own host, is
 * reached only by a Super Admin, and reads churches it does not belong to —
 * none of which the member app should be able to do by importing the wrong
 * hook. The API client underneath is shared, so refresh, error shapes and the
 * host→API mapping stay identical.
 *
 * VOCABULARY: the API path is /groups, because the client's specification
 * calls a church a Group. Everything a person reads says church.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './client'
import type {
  ChurchDetail,
  ChurchFeatures,
  ChurchRequest,
  ChurchStatus,
  ChurchSummary,
  CustomField,
  CustomFieldDraft,
  Member,
  NumberedPage,
  Page,
} from '@/types/api'

export const consoleKeys = {
  churches: (status: string, search: string, page: number) =>
    ['console', 'churches', status, search, page] as const,
  church: (id: string) => ['console', 'church', id] as const,
  requests: ['console', 'requests'] as const,
  churchMembers: (id: string, q = '', cursor = '') =>
    ['console', 'church', id, 'members', q, cursor] as const,
  adminStatus: (id: string) => ['console', 'church', id, 'admin-status'] as const,
  churchFields: (id: string) => ['console', 'church', id, 'fields'] as const,
}

/** The churches list, paged by the server.
 *
 *  Returns the whole page rather than just the items: the list needs `total`
 *  to render "1–20 of 84" and to know whether Next is reachable. */
export function useChurches(params: {
  status: ChurchStatus | 'all'
  search: string
  page: number
  pageSize?: number
}) {
  const { status, search, page, pageSize = 20 } = params
  return useQuery({
    queryKey: consoleKeys.churches(status, search, page),
    queryFn: () => {
      const q = new URLSearchParams()
      if (status !== 'all') q.set('status', status)
      if (search) q.set('search', search)
      q.set('page', String(page))
      q.set('page_size', String(pageSize))
      return api.get<NumberedPage<ChurchSummary>>(`/groups?${q}`)
    },
    // Paging feels broken if the list blanks between pages, so the previous
    // page stays on screen until the next one arrives.
    placeholderData: (prev) => prev,
  })
}

export function useChurch(id: string | null) {
  return useQuery({
    queryKey: consoleKeys.church(id ?? ''),
    queryFn: () => api.get<ChurchDetail>(`/groups/${id}`),
    enabled: Boolean(id),
  })
}

/** One page of a church's members, searchable. The API pages by cursor;
 *  the tab keeps the cursors it has passed so Previous works. */
export const MEMBERS_PAGE_SIZE = 25

export function useChurchMembers(id: string | null, q = '', cursor = '') {
  return useQuery({
    queryKey: consoleKeys.churchMembers(id ?? '', q, cursor),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(MEMBERS_PAGE_SIZE) })
      if (q) params.set('q', q)
      if (cursor) params.set('cursor', cursor)
      return api.get<Page<Member> & { total?: number | null }>(
        `/groups/${id}/members?${params}`,
      )
    },
    enabled: Boolean(id),
    placeholderData: (prev) => prev,
  })
}

export interface AdminStatus {
  status: string
  pending_request: {
    id: string
    name: string
    mobile: string | null
    email: string | null
    requested_at: string
  } | null
  invited_account: {
    id: string
    name: string
    mobile: string | null
    email: string | null
    in_another_church: boolean
  } | null
}

export function useAdminStatus(id: string, enabled: boolean) {
  return useQuery({
    queryKey: consoleKeys.adminStatus(id),
    queryFn: () => api.get<AdminStatus>(`/groups/${id}/admin-status`),
    enabled,
  })
}

/** Approve the waiting request, or make the invited contact's account the
 *  admin when they signed up without the link. */
export function useAppointAdmin(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<unknown>(`/groups/${id}/appoint-admin`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: consoleKeys.church(id) })
      qc.invalidateQueries({ queryKey: ['console', 'churches'] })
      qc.invalidateQueries({ queryKey: consoleKeys.requests })
    },
  })
}

/** Churches awaiting approval. Super Admin only — a Church Admin gets a 403,
 *  which the Requests page turns into an explanation rather than an error. */
export function useChurchRequests() {
  return useQuery({
    queryKey: consoleKeys.requests,
    queryFn: async () =>
      (await api.get<Page<ChurchRequest>>('/groups/requests')).items,
  })
}

/** The church's own signup fields, on top of the standard ones. */
export function useChurchFields(id: string | null) {
  return useQuery({
    queryKey: consoleKeys.churchFields(id ?? ''),
    queryFn: async () =>
      (await api.get<Page<CustomField>>(`/groups/${id}/fields`)).items,
    enabled: Boolean(id),
  })
}

/** Add a field to an existing church.
 *
 *  There is no delete endpoint, and that is deliberate on the server: people
 *  have already answered these questions, so removing a field would orphan
 *  their answers. Fields can be removed while DRAFTING a church, before anyone
 *  has filled one in — after that they are permanent. */
export function useAddChurchField(churchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (field: CustomFieldDraft) =>
      api.post<{ id: string }>(`/groups/${churchId}/fields`, field),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: consoleKeys.churchFields(churchId) })
    },
  })
}

/** Create a church.
 *
 *  Returns the signup URL rather than the church: that link is the whole point
 *  of creating one — it is what gets sent to the person who will administer
 *  it — so the create screen shows it and offers to copy it. */
export function useCreateChurch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateChurchInput) =>
      api.post<CreateChurchResult>('/groups', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['console', 'churches'] })
    },
  })
}

/** Mirrors CreateChurchIn on the server. Only `name` is required — a church
 *  can be created before its admin is known, which is what the awaiting_admin
 *  status exists for. */
export interface CreateChurchInput {
  name: string
  admin_mobile?: string | null
  admin_email?: string | null
  city?: string | null
  state?: string | null
  address?: string | null
  plan?: 'basic' | 'standard' | 'professional'
  welcome_text?: string | null
  signature_text?: string | null
  members_can_see_others?: boolean
  features?: Partial<ChurchFeatures>
  /** Sent WITH the church: the fields are part of creating it, not a second
   *  step somebody has to remember. */
  custom_fields?: CustomFieldDraft[]
  /** The Group URL. Left out, the server makes one from the name. */
  slug?: string
}

export interface CreateChurchResult {
  church_id: string
  slug: string
  signup_url: string
  welcome_text: string
}

export function useUpdateChurch(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (changes: Partial<{
      name: string
      city: string | null
      state: string | null
      address: string | null
      admin_mobile: string | null
      admin_email: string | null
      plan: 'basic' | 'standard' | 'professional'
      welcome_text: string | null
      signature_text: string | null
      members_can_see_others: boolean
      /** Partial: only the keys sent change. */
      features: Partial<ChurchFeatures>
      /** Super Admin only. The old link keeps working. */
      slug: string
    }>) => api.patch<ChurchDetail>(`/groups/${id}`, changes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: consoleKeys.church(id) })
      qc.invalidateQueries({ queryKey: ['console', 'churches'] })
    },
  })
}

/** Move a church through its lifecycle.
 *
 *  Only these three can be SET. `awaiting_admin` is missing deliberately: the
 *  system puts a church there when it has no registered admin, and nothing is
 *  achieved by an admin choosing it by hand — the server rejects it anyway.
 *
 *  "Deleted" is a status, not a removal — a church that actually vanished
 *  would take its members' posts and prayer requests with it. */
export type SettableStatus = Extract<ChurchStatus, 'active' | 'on_hold' | 'deleted'>

export function useSetChurchStatus(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (status: SettableStatus) =>
      api.post<{ status: string }>(`/groups/${id}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: consoleKeys.church(id) })
      qc.invalidateQueries({ queryKey: ['console', 'churches'] })
    },
  })
}

export function useDecideRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { requestId: string; approve: boolean; note?: string }) =>
      api.post<{ request_id: string; church_id: string; status: string }>(
        `/groups/requests/${input.requestId}/decide`,
        { approve: input.approve, note: input.note ?? null },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: consoleKeys.requests })
      // An approval creates a live church, so the list is stale too.
      qc.invalidateQueries({ queryKey: ['console', 'churches'] })
    },
  })
}
